// src/services/chatClear.js — clear a chat, with honest sizes.
//
// ── WHAT "CLEAR" MEANS, AND WHERE IT REACHES ────────────────────────────────
// Clearing everything writes the app's own per-user watermark:
//
//     chats/{id}.clearedAt.{myUid} = <ms>
//
// That is the field ChatInfoScreen.performClearChatCore writes and that every
// surface already filters on — this chat, the chat list preview, the web's
// search, the media strip. So a clear made here empties the conversation on
// your phone too, and nobody else's copy is touched (it is keyed by MY uid).
//
// Clearing only MEDIA writes a second, parallel watermark:
//
//     chats/{id}.mediaClearedAt.{myUid} = <ms>
//
// 🟥 A NEW FIELD, AND THE HONEST REASON FOR IT. The phone's media clear deletes
//    FILES from its own disk — a local act, which is why nothing in Firestore
//    recorded it. (It still reaches the web: the collapsed rows ship over in the
//    history sync carrying `mediaDeletedFromStorage`, which the web already
//    honours. App → web was never the gap.) A browser has no such files: its
//    media lives in the cloud and is fetched per view, so the only thing it can
//    express is a per-user watermark. This writes it, the web honours it
//    everywhere it draws media, and the phone honours it too —
//    services/mediaClearSync.ts on the app side watches this field and, when it
//    advances, deletes that chat's local files and collapses the messages to the
//    app's own placeholder. So both directions are closed.
//
//    Written as serverTimestamp(), not Date.now(): the phone reads these fields
//    as Firestore Timestamps and a bare number would throw in its chat-doc
//    listener.
//
// ── THE CLOCK-SKEW GUARD ────────────────────────────────────────────────────
// Copied from the app deliberately: the watermark is at least (newest message
// + 1ms), never bare Date.now(). Messages carry SERVER timestamps, so a device
// running a little behind would write a clear time OLDER than its newest
// messages — they'd survive the filter and the chat would look like it refused
// to clear. The app hit this hardest in Note to Self; the web would too.

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { tsMs, clearedAtMs } from './chats';

export const MEDIA_KINDS = [
  { id: 'photos', name: 'Photos' },
  { id: 'videos', name: 'Videos' },
  { id: 'voice', name: 'Voice messages' },
  { id: 'docs', name: 'Documents' },
];

/** Every fetchable media URL a message carries. */
function urlsOf(m) {
  const out = [];
  const push = (u) => { if (typeof u === 'string' && /^https?:/i.test(u)) out.push(u); };
  push(m.url); push(m.imageUrl); push(m.videoUrl); push(m.audioUrl); push(m.documentUrl);
  (m.images || []).forEach(push);
  return out;
}

export function kindOf(m) {
  switch (m?.type) {
    case 'image': case 'images': case 'sticker': case 'gif': return 'photos';
    case 'video': return 'videos';
    case 'audio': return 'voice';
    case 'document': return 'docs';
    default: return null;
  }
}

/**
 * Measure what a clear would reclaim.
 *
 * Documents carry their real size on the message (documentSize). For everything
 * else the size is only known to whoever holds the bytes, so we ASK: a HEAD
 * request per media URL, read Content-Length. That's a real measurement rather
 * than a guess — and when CORS or the CDN refuses it we say "unknown" instead
 * of inventing a number.
 *
 * @returns { counts, bytes, measured, unknown, textBytes }
 */
export async function scanChat(msgs, chat, myUid, { onProgress, signal } = {}) {
  const cleared = clearedAtMs(chat, myUid);
  const live = (msgs || []).filter((m) => {
    if (!m || m.isDeleted || m.deleted) return false;
    if (cleared && tsMs(m.timestamp) <= cleared) return false;
    return true;
  });

  const counts = { photos: 0, videos: 0, voice: 0, docs: 0 };
  const bytes = { photos: 0, videos: 0, voice: 0, docs: 0 };
  let unknown = 0;
  let measured = 0;

  // text weight: the transcript's own footprint, so "clear everything" can say
  // something truthful even in a chat with no media at all
  const textBytes = live.reduce((a, m) => a + new Blob([String(m._plain || m.text || '')]).size, 0);

  const jobs = [];
  for (const m of live) {
    const k = kindOf(m);
    if (!k) continue;
    counts[k] += 1;
    if (k === 'docs' && Number(m.documentSize)) { bytes.docs += Number(m.documentSize); measured += 1; continue; }
    const us = urlsOf(m);
    if (!us.length) { unknown += 1; continue; }
    us.forEach((u) => jobs.push({ k, url: u }));
  }

  // measure with a small concurrency cap — a media-heavy chat shouldn't fire
  // two hundred requests at once
  let done = 0;
  const CONC = 8;
  let i = 0;
  const worker = async () => {
    while (i < jobs.length) {
      const job = jobs[i]; i += 1;
      if (signal?.aborted) return;
      try {
        const r = await fetch(job.url, { method: 'HEAD' });
        const len = Number(r.headers.get('content-length'));
        if (Number.isFinite(len) && len > 0) { bytes[job.k] += len; measured += 1; } else unknown += 1;
      } catch { unknown += 1; }
      done += 1;
      onProgress?.(done, jobs.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONC, jobs.length) }, worker));

  return { counts, bytes, measured, unknown, textBytes, total: live.length };
}

/** The clear time: never older than the newest message (see the note above). */
export function clearStamp(msgs) {
  let now = Date.now();
  for (const m of msgs || []) {
    const t = tsMs(m.timestamp);
    if (t && t >= now) now = t + 1;
  }
  return now;
}

// 🟥 WRITE A SERVER TIMESTAMP, NOT A NUMBER. The phone reads this field as
//    `data.clearedAt[uid].toDate()` — a bare millisecond number has no
//    .toDate() and would throw inside ChatModal's chat-doc snapshot handler,
//    taking the whole listener down. serverTimestamp() is also the stronger
//    value: it comes from the same clock the message timestamps do, so it is
//    ≥ every existing message by construction and the skew guard above becomes
//    belt-and-braces rather than load-bearing. (clearStamp is still exported —
//    it's what the local, immediate filtering uses before the server answers.)

/** Clear EVERYTHING — the app's own watermark, so every device follows. */
export async function clearEverything(chatId, myUid, msgs) {
  const at = clearStamp(msgs);
  await updateDoc(doc(db, 'chats', chatId), {
    [`clearedAt.${myUid}`]: serverTimestamp(),
    [`mediaClearedAt.${myUid}`]: serverTimestamp(),   // media is part of "everything"
    [`unreadCounts.${myUid}`]: 0,
  });
  return at;
}

/** Clear only the media. Web-honoured today; see the note at the top. */
export async function clearMediaOnly(chatId, myUid, msgs) {
  const at = clearStamp(msgs);
  await updateDoc(doc(db, 'chats', chatId), { [`mediaClearedAt.${myUid}`]: serverTimestamp() });
  return at;
}

/** The media watermark, read the way clearedAtMs reads its own. */
export function mediaClearedMs(chat, uid) {
  const c = chat?.mediaClearedAt;
  if (!c || !uid) return 0;
  return tsMs(c[uid]);
}

/** Has this message's media been cleared for me? */
export function isMediaCleared(m, chat, uid) {
  const at = mediaClearedMs(chat, uid);
  if (!at) return false;
  if (!kindOf(m)) return false;
  return tsMs(m.timestamp) <= at;
}
